export type PermissionStatus = "allowed" | "denied" | "restricted";

export interface Permission {
  label: string;
  status: PermissionStatus;
}

export interface ProfilePermissions {
  permissions: Permission[];
  note?: string;
}

export const BANKING_PERMISSIONS_MAP: Record<string, ProfilePermissions> = {
  ADMIN: {
    permissions: [
      { label: "Visualizar saldo, conta e extrato completo", status: "allowed" },
      { label: "Criar e gerenciar chave Pix", status: "allowed" },
      { label: "Sacar recursos da conta", status: "allowed" },
      { label: "Investir e resgatar dinheiro", status: "allowed" },
      { label: "Configurar e aprovar pagamentos", status: "allowed" },
      { label: "Visualizar saldo, conta e extrato completo", status: "allowed" },
      { label: "Criar, cancelar e bloquear cartões", status: "allowed" },
      { label: "Visualizar e pagar faturas de cartão", status: "allowed" },
    ],
    note: "Esse perfil também pode gerar relatórios, criar usuários, visualizar logs e gerar chaves de API.",
  },
  OPERADOR: {
    permissions: [
      { label: "Visualizar saldo, conta e extrato completo", status: "allowed" },
      { label: "Solicitar pagamentos", status: "allowed" },
      { label: "Visualizar cartões e limites", status: "allowed" },
      { label: "Sem permissão para sacar, aprovar ou configurar pagamentos", status: "denied" },
      { label: "Sem permissão para criar, cancelar ou bloquear cartões", status: "denied" },
    ],
    note: "Este perfil também não acessa nenhuma funcionalidade administrativa.",
  },
  FINANCEIRO: {
    permissions: [
      { label: "Visualizar saldo, conta e extrato completo", status: "allowed" },
      { label: "Criar chave Pix e sacar recursos", status: "allowed" },
      { label: "Investir e resgatar dinheiro", status: "allowed" },
      { label: "Efetuar transferências e solicitar pagamentos", status: "allowed" },
      { label: "Criar e visualizar cartões", status: "allowed" },
      { label: "Não pode aprovar pagamentos", status: "denied" },
    ],
    note: "Este perfil não acessa funcionalidades administrativas como relatórios, criação de usuários ou visualização de logs.",
  },
};

export const ACQUIRING_PERMISSIONS_MAP: Record<string, ProfilePermissions> = {
  ADMIN: {
    permissions: [
      { label: "Dashboard", status: "allowed" },
      { label: "Links de Pagamento (Pontual e Assinaturas)", status: "allowed" },
      { label: "Cobranças", status: "allowed" },
      { label: "Clientes", status: "allowed" },
      { label: "Financeiro", status: "allowed" },
      { label: "Relatórios", status: "allowed" },
      { label: "Antecipação", status: "allowed" },
      { label: "Visualização de Notificações", status: "allowed" },
      { label: "Configurações e Integrações", status: "allowed" },
    ],
    note: "Acesso total a todas as funcionalidades do sistema.",
  },
  MANAGER: {
    permissions: [
      { label: "Dashboard", status: "allowed" },
      { label: "Links de Pagamento (Pontual e Assinaturas)", status: "allowed" },
      { label: "Cobranças", status: "allowed" },
      { label: "Clientes", status: "allowed" },
      { label: "Financeiro", status: "allowed" },
      { label: "Relatórios", status: "allowed" },
      { label: "Antecipação", status: "allowed" },
      { label: "Visualização de Notificações", status: "allowed" },
      { label: "Configurações e Integrações", status: "denied" },
    ],
    note: "Este perfil possui acesso completo às operações de cobrança, relatórios e antecipação, mas sem permissões administrativas.",
  },
  FINANCE: {
    permissions: [
      { label: "Dashboard", status: "allowed" },
      { label: "Clientes", status: "allowed" },
      { label: "Financeiro", status: "allowed" },
      { label: "Relatórios", status: "allowed" },
      { label: "Antecipação", status: "allowed" },
      { label: "Visualização de Notificações", status: "allowed" },
      { label: "Cobranças", status: "denied" },
      { label: "Links de Pagamento", status: "denied" },
    ],
    note: "Focado em rotinas financeiras e relatórios, sem acesso a criar cobranças ou links de pagamento.",
  },
  SUPERVISOR: {
    permissions: [
      { label: "Links de Pagamento (Pontual e Assinaturas)", status: "allowed" },
      { label: "Cobranças", status: "allowed" },
      { label: "Clientes", status: "allowed" },
      { label: "Visualização de Notificações", status: "allowed" },
      { label: "Financeiro", status: "denied" },
      { label: "Relatórios", status: "denied" },
      { label: "Antecipação", status: "denied" },
    ],
    note: "Restrito a operações de clientes e cobranças, sem acesso financeiro ou relatórios.",
  },
  SALE: {
    permissions: [
      { label: "Links de Pagamento (Pontual e Assinaturas)", status: "allowed" },
      { label: "Cobranças", status: "allowed" },
      { label: "Clientes", status: "allowed" },
      { label: "Financeiro", status: "denied" },
      { label: "Relatórios", status: "denied" },
      { label: "Antecipação", status: "denied" },
      { label: "Visualização de Notificações", status: "denied" },
    ],
    note: "Perfil voltado para vendas: pode lidar com clientes e cobranças, mas sem acesso a relatórios ou informações financeiras.",
  },
};
